import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useResetPassword } from '@/features/auth';
import { Box, Form } from '@/shared/ui';
import { FormFieldValues, FormGenerator } from '@/shared/ui/FormGenerator';
import { Logo } from '@/shared/ui/Logo';
import { RouterLink } from '@/shared/ui/RouterLink';
import { CustomTypography } from '@/shared/ui/Typography';

import { createResetPasswordSchema } from '../model/schemas';

import { StyledBox } from './styles';

export const ResetPasswordForm = () => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(['common']);
  const token = searchParams.get('token');

  const defaultValues = {
    code: '',
    password: '',
    confirmPassword: '',
  };

  const codeFormScheme = [
    {
      name: 'code',
      label: t('auth:resetPassword.code.label'),
      type: FormFieldValues.Default,
      inputProps: {
        id: 'reset-password-code',
        placeholder: t('auth:resetPassword.code.helpText'),
        autoComplete: 'one-time-code',
        inputProps: {
          inputMode: 'numeric',
          maxLength: 6,
          pattern: '[0-9]*',
          'aria-details': 'reset-password-code-help',
        },
      },
    },
  ];

  const passwordFormScheme = [
    {
      name: 'password',
      label: t('common:inputs.password'),
      type: FormFieldValues.Password,
      inputProps: {
        placeholder: t('common:inputs.placeholders.password'),
      },
    },
    {
      name: 'confirmPassword',
      label: t('common:inputs.confirmPassword'),
      type: FormFieldValues.Password,
      inputProps: {
        placeholder: t('common:inputs.placeholders.confirmPassword'),
      },
      dependencies: ['confirmPassword', 'password'],
    },
  ];

  const mutation = useResetPassword();

  const onSubmit = async (data: {
    code: string;
    password: string;
    confirmPassword: string;
  }) => {
    if (!token) return;

    await mutation.resetPassword({
      token,
      code: data.code,
      new_password: data.password,
    });
  };

  return (
    <StyledBox>
      <Logo />
      <CustomTypography variant="h5" sx={{ mb: 3, mt: 2, fontWeight: 600 }}>
        {t('auth:resetPassword.title')}
      </CustomTypography>
      {token ? (
        <Box sx={{ maxWidth: 440, width: '100%' }}>
          <Form
            schema={createResetPasswordSchema(t)}
            onSubmit={onSubmit}
            defaultValues={defaultValues}
            mode="onTouched"
            submitBtnText={t('common:buttons.save')}
          >
            <FormGenerator formScheme={codeFormScheme} />
            <CustomTypography
              id="reset-password-code-help"
              variant="body2"
              color="textSecondary"
              sx={{ mt: 1 }}
            >
              {t('auth:resetPassword.code.helpText')}
            </CustomTypography>
            <FormGenerator formScheme={passwordFormScheme} />
          </Form>
        </Box>
      ) : (
        <Box sx={{ maxWidth: 440, textAlign: 'center' }}>
          <CustomTypography variant="body1">
            {t('auth:resetPassword.invalidLink')}
          </CustomTypography>
          <Box mt={1}>
            <RouterLink variant="body2" to="/auth/forgot-password">
              {t('auth:login.forgotPassword')}
            </RouterLink>
          </Box>
        </Box>
      )}

      <Box mt={1}>
        <RouterLink variant="body2" to="/auth/login">
          {t('auth:common.backToLogin')}
        </RouterLink>
      </Box>
    </StyledBox>
  );
};
